import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Table, Button, Drawer, Form, Input, Select, Space, Popconfirm, message } from 'antd';
import { EditOutlined, DeleteOutlined, PlusOutlined, SearchOutlined } from '@ant-design/icons';
import { useAuth } from '../../../contexts/AuthContext';
import { PERMISSIONS } from '../../../config/permissions';
import PageBreadcrumbs from '../../../components/common/PageBreadcrumbs';
import attributeCatalogService from '../../../services/attributeCatalogService';
import { ATTRIBUTE_CATEGORIES } from '../../../config/attributeCategories';
import useBreakpoint from '../../../hooks/useBreakpoint';

const { Option } = Select;

/**
 * Pre-IPO Attribute Catalog Page
 * Full CRUD for attributes with permission-based UI
 */
export default function AttributeCatalogPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { hasPermission, isSuperAdmin } = useAuth();
  const canCreate = isSuperAdmin || hasPermission(PERMISSIONS.PRODUCTS_CREATE);
  const canEdit = isSuperAdmin || hasPermission(PERMISSIONS.PRODUCTS_EDIT);
  const canDelete = isSuperAdmin || hasPermission(PERMISSIONS.PRODUCTS_DELETE);

  const [attributes, setAttributes] = useState([]);
  const [dataTypes, setDataTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [selectedAttribute, setSelectedAttribute] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [tableSearchText, setTableSearchText] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [form] = Form.useForm();
  const isAboveSm = useBreakpoint('sm');

  const isEditMode = !!selectedAttribute;

  useEffect(() => {
    fetchAttributes();
    fetchDataTypes();
  }, [categoryFilter]);

  useEffect(() => {
    const state = location.state;
    if (state?.openCreate && state?.category != null) {
      navigate(location.pathname, { replace: true, state: {} });
      setSelectedAttribute(null);
      form.resetFields();
      form.setFieldsValue({ category: state.category });
      setDrawerVisible(true);
    }
  }, [location.state]);

  const fetchAttributes = async () => {
    setLoading(true);
    try {
      const params = { type: 'PRE_IPO' };
      if (categoryFilter && categoryFilter.trim()) params.category = categoryFilter.trim();
      const result = await attributeCatalogService.getAttributes(params);
      if (result.success) {
        setAttributes(Array.isArray(result.data) ? result.data : []);
      } else {
        message.error(result.message || 'Failed to fetch attributes');
        setAttributes([]);
      }
    } catch (error) {
      message.error('Error loading attributes');
      console.error('Error fetching attributes:', error);
      setAttributes([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchDataTypes = async () => {
    try {
      const result = await attributeCatalogService.getDataTypes();
      if (result.success && Array.isArray(result.data)) {
        setDataTypes(result.data);
      }
    } catch (error) {
      console.error('Error fetching data types:', error);
    }
  };

  const getDataTypeLabel = (value) => {
    const dt = dataTypes.find((d) => (d.value || '').toUpperCase() === (value || '').toUpperCase());
    return dt?.label || value || '-';
  };

  const filteredAttributes = tableSearchText.trim()
    ? attributes.filter(
        (a) =>
          (a.label || '').toLowerCase().includes(tableSearchText.toLowerCase()) ||
          (a.key || '').toLowerCase().includes(tableSearchText.toLowerCase())
      )
    : attributes;

  const openCreateDrawer = () => {
    setSelectedAttribute(null);
    form.resetFields();
    setDrawerVisible(true);
  };

  const handleEdit = (record) => {
    setSelectedAttribute(record);
    form.setFieldsValue({
      label: record.label || '',
    });
    setDrawerVisible(true);
  };

  const handleDelete = async (id) => {
    try {
      const result = await attributeCatalogService.deleteAttribute(id);
      if (result.success) {
        message.success('Attribute deleted successfully');
        fetchAttributes();
      } else {
        message.error(result.message || 'Failed to delete attribute');
      }
    } catch (error) {
      message.error(error?.response?.data?.message || 'Error deleting attribute');
      console.error('Error deleting attribute:', error);
    }
  };

  const handleSubmit = async (values) => {
    setSubmitting(true);
    try {
      if (isEditMode) {
        const result = await attributeCatalogService.updateAttribute(selectedAttribute.id, {
          label: values.label,
        });
        if (result.success) {
          message.success('Attribute updated successfully');
          setDrawerVisible(false);
          setSelectedAttribute(null);
          form.resetFields();
          fetchAttributes();
        } else {
          message.error(result.message || 'Failed to update attribute');
        }
      } else {
        const payload = {
          product_type_id: '1',
          key: (values.key || '').trim().toLowerCase().replace(/\s+/g, '_'),
          label: values.label?.trim(),
          data_type: values.data_type,
          category: values.category,
        };
        if (values.data_type === 'ENUM' && values.enum_values?.trim()) {
          payload.validation = {
            enum_values: values.enum_values
              .split(',')
              .map((v) => v.trim().toUpperCase())
              .filter(Boolean),
          };
        }
        const result = await attributeCatalogService.createAttribute(payload);
        if (result.success) {
          message.success('Attribute created successfully');
          setDrawerVisible(false);
          setSelectedAttribute(null);
          form.resetFields();
          fetchAttributes();
        } else {
          message.error(result.message || 'Failed to create attribute');
        }
      }
    } catch (error) {
      const errMsg = error?.response?.data?.message || error?.message;
      message.error(errMsg || (isEditMode ? 'Error updating attribute' : 'Error creating attribute'));
      console.error(isEditMode ? 'Error updating attribute:' : 'Error creating attribute:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const columns = [
    {
      title: 'Display Label',
      dataIndex: 'label',
      key: 'label',
      sorter: (a, b) => (a.label || '').localeCompare(b.label || ''),
      render: (text) => <span className="font-medium text-gray-900">{text || '-'}</span>,
    },
    {
      title: 'Key Name',
      dataIndex: 'key',
      key: 'key',
      sorter: (a, b) => (a.key || '').localeCompare(b.key || ''),
      render: (text) => (
        <span className="inline-flex items-center px-2 py-0.5 rounded text-sm font-mono bg-blue-50 text-blue-700">
          {text || '-'}
        </span>
      ),
    },
    {
      title: 'Data Type',
      dataIndex: 'data_type',
      key: 'data_type',
      render: (text) => (
        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-gray-100 text-gray-700">
          {getDataTypeLabel(text)}
        </span>
      ),
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      render: (text) => text || '-',
    },
    ...(canEdit || canDelete
      ? [
          {
            title: 'Actions',
            key: 'actions',
            width: isAboveSm ? 130 : 110,
            fixed: isAboveSm ? 'right' : undefined,
            render: (_, record) => (
              <Space size={isAboveSm ? 'small' : 'middle'} wrap>
                {canEdit && (
                  <Button
                    icon={<EditOutlined />}
                    size={isAboveSm ? 'small' : 'middle'}
                    onClick={() => handleEdit(record)}
                    title="Edit Attribute"
                    className={!isAboveSm ? 'px-2!' : ''}
                  />
                )}
                {canDelete && (
                  <Popconfirm
                    title="Delete Attribute"
                    description="Are you sure you want to delete this attribute? This action cannot be undone."
                    onConfirm={() => handleDelete(record.id)}
                    okText="Yes, Delete"
                    cancelText="Cancel"
                    okButtonProps={{ danger: true }}
                  >
                    <Button
                      danger
                      icon={<DeleteOutlined />}
                      size={isAboveSm ? 'small' : 'middle'}
                      title="Delete Attribute"
                      className={!isAboveSm ? 'px-2!' : ''}
                    />
                  </Popconfirm>
                )}
              </Space>
            ),
          },
        ]
      : []),
  ];

  return (
    <div className="bg-white rounded-lg shadow-sm overflow-hidden min-h-0 flex flex-col flex-1 h-full">
      <div className="shrink-0">
        <PageBreadcrumbs />
      </div>

      <div className="p-4 sm:p-6 flex-1 min-h-0 overflow-y-auto">
        {/* Header */}
        <div className="mb-4 flex flex-wrap justify-between items-center gap-3">
          <div>
            <h1 className="text-xl font-semibold text-gray-900">Pre-IPO Attribute Catalog</h1>
            <p className="text-sm text-gray-600 mt-1">Manage attributes for Pre-IPO products</p>
          </div>
          {canCreate && (
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreateDrawer} size="large">
              Add Attribute
            </Button>
          )}
        </div>

        {/* Filters */}
        <div className="mb-4 flex flex-wrap gap-3">
          <Input.Search
            placeholder="Search by label or key..."
            allowClear
            enterButton={
              <>
                <SearchOutlined /> Search
              </>
            }
            value={tableSearchText}
            onChange={(e) => setTableSearchText(e.target.value)}
            onSearch={setTableSearchText}
            className="max-w-md"
          />
          <Select
            placeholder="All Categories"
            allowClear
            value={categoryFilter || undefined}
            onChange={(v) => setCategoryFilter(v ?? '')}
            className="w-48"
            options={[
              { value: '', label: 'All Categories' },
              ...ATTRIBUTE_CATEGORIES.map((c) => ({ value: c.value, label: c.label })),
            ]}
          />
        </div>

        {/* Table */}
        <Table
          columns={columns}
          dataSource={filteredAttributes}
          loading={loading}
          rowKey="id"
          scroll={{ x: 800 }}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} attributes`,
            showQuickJumper: true,
          }}
          className="attribute-catalog-table"
        />

        <style>{`
          .attribute-catalog-table .ant-table-thead > tr > th {
            background: #F9FAFB;
            font-weight: 600;
            color: #374151;
            border-bottom: 2px solid #E5E7EB;
          }
          .attribute-catalog-table .ant-table-tbody > tr:hover > td {
            background: #F3F4F6;
          }
        `}</style>

        {/* Create / Edit Drawer */}
        <Drawer
          title={isEditMode ? 'Edit Attribute' : 'Create New Attribute'}
          open={drawerVisible}
          onClose={() => {
            setDrawerVisible(false);
            setSelectedAttribute(null);
            form.resetFields();
          }}
          width={500}
          destroyOnClose
          footer={null}
        >
          <Form form={form} layout="vertical" onFinish={handleSubmit}>
            <Form.Item
              label="Display Label"
              name="label"
              rules={[{ required: true, message: 'Please enter display label' }]}
            >
              <Input placeholder="e.g. Annual Revenue" />
            </Form.Item>

            {!isEditMode && (
              <>
                <Form.Item
                  label="Key Name"
                  name="key"
                  rules={[
                    { required: true, message: 'Please enter key name' },
                    {
                      pattern: /^[a-z0-9_]+$/,
                      message: 'Key must be lowercase letters, numbers, and underscores only (e.g., annual_revenue)',
                    },
                  ]}
                >
                  <Input placeholder="e.g. annual_revenue" />
                </Form.Item>

                <Form.Item
                  label="Value Type"
                  name="data_type"
                  rules={[{ required: true, message: 'Please select value type' }]}
                >
                  <Select placeholder="Select value type" options={dataTypes.map((d) => ({ value: d.value, label: d.label }))} />
                </Form.Item>

                <Form.Item
                  label="Category"
                  name="category"
                  rules={[{ required: true, message: 'Please select category' }]}
                >
                  <Select
                    placeholder="Select category"
                    options={ATTRIBUTE_CATEGORIES.map((c) => ({ value: c.value, label: c.label }))}
                  />
                </Form.Item>

                <Form.Item
                  noStyle
                  shouldUpdate={(prev, curr) => prev.data_type !== curr.data_type}
                >
                  {({ getFieldValue }) =>
                    getFieldValue('data_type') === 'ENUM' ? (
                      <Form.Item
                        label="Enum Values"
                        name="enum_values"
                        help="Comma-separated values (e.g. HIGH, MEDIUM, LOW)"
                      >
                        <Input placeholder="e.g. HIGH, MEDIUM, LOW" />
                      </Form.Item>
                    ) : null
                  }
                </Form.Item>

                <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-800 mb-4">
                  Note: Choose Key Name and Value Type carefully. They cannot be changed once the attribute is created.
                </div>
              </>
            )}

            <Form.Item>
              <Space>
                <Button type="primary" htmlType="submit" loading={submitting}>
                  {isEditMode ? 'Update' : 'Create'}
                </Button>
                <Button
                  onClick={() => {
                    setDrawerVisible(false);
                    setSelectedAttribute(null);
                    form.resetFields();
                  }}
                  disabled={submitting}
                >
                  Cancel
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Drawer>
      </div>
    </div>
  );
}
